import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { env } from '@/utils/constants/env';
import axios from 'axios';
import useTenantState from './tenant.state';
import { keycloakAuth, KeycloakUserInfo } from '@/utils/auth/keycloak';

type Store = {
  logout: () => Promise<void>;
  userProfile?: UserProfile;
  keycloakUserInfo?: KeycloakUserInfo;
  tenants?: Tenant[];
  loadUserProfile: (token?: string, force?: boolean) => Promise<void>;
  updateUserProfileLocal: (payload: Partial<UserProfile>) => void;
  loadTenants: (token?: string) => Promise<Tenant[]>;
  lastProfileUpdate?: Date;
  handleLogin: () => Promise<void>;
  handleLoginWithJoin: (action: string, token: string) => Promise<void>;
  getToken: () => Promise<string | null>; // Get current token from Keycloak
  getValidToken: () => Promise<string | null>;
  handleCallback: () => Promise<void>;
  isLogoutLoading?: boolean;
  currentRole: string;
  setCurrentRole: (role: string) => void;
};
export interface UserProfile {
  id: number;
  name: string;
  email: string;
  public_name: string;
  avatar_url: string;
  is_admin: boolean;
  email_verified_at: Date;
  created_at: Date;
  updated_at: Date;
  tenants: Tenant[];
}

export interface Tenant {
  id: number;
  slug: string;
  name: string;
  uuid: string;
  userId: number;
  logoPath: string;
  role?: string; // Role for SaaS (e.g., "tenancy:member", "tenancy:owner")
  faviconPath: string;
}

const useAuthState = create<Store>()(
  persist(
    (set, get) => ({
      currentRole: '',
      setCurrentRole: (role: string) => {
        set({ currentRole: role.toLowerCase() });
      },
      lastProfileUpdate: undefined,
      isLogoutLoading: false,
      keycloakUserInfo: undefined,
      updateUserProfileLocal: payload => {
        set(state => ({
          userProfile: state.userProfile
            ? structuredClone({ ...state.userProfile, ...payload })
            : state.userProfile,
        }));
      },
      loadUserProfile: async token => {
        // Get token from Keycloak if not provided
        const activeToken = token || await keycloakAuth.getValidToken();
        if (!activeToken) {
          console.warn('No token available for loading user profile');
          return;
        }

        try {
          // Always use backend API endpoint for user profile
          const userInfo = await keycloakAuth.getUserInfo(activeToken);
          console.log('User profile loaded from backend:', userInfo);
          
          const userInfoFromKC = {
            id: parseInt(userInfo.sub) || 0, // Use sub as ID, fallback to 0
            name: userInfo.name || `${userInfo.given_name || ''} ${userInfo.family_name || ''}`.trim() || userInfo.preferred_username || 'Unknown',
            email: userInfo.email,
            public_name: '-',
            avatar_url: userInfo.picture || userInfo.avatar_url || '-',
            is_admin: false,
            email_verified_at: new Date(),
            created_at: new Date(), // Keycloak doesn't provide these by default
            updated_at: new Date(),
            tenant_role: userInfo.tenant_role,
            tenants: [],
          } as UserProfile;
          
          set({
            keycloakUserInfo: userInfo,
            lastProfileUpdate: new Date(),
            userProfile: userInfoFromKC,
            currentRole: userInfo.tenant_role?.toLowerCase() || '',
          });
        } catch (error) {
          console.error('Error loading user profile:', error);
          globalThis.location.href = `/login`;
          localStorage.removeItem('auth-storage');
          throw error;
        }
      },


      // Get current token from Keycloak instance
      getToken: async () => {
        return await keycloakAuth.getValidToken() || null;
      },

      getValidToken: async () => {
        // Keycloak manages token refresh internally
        return await keycloakAuth.getValidToken() || null;
      },
      loadTenants: async (token?: string) => {
        // Get token from Keycloak if not provided
        const activeToken = token || await keycloakAuth.getValidToken();

        if (!activeToken) {
          console.warn('No token available for loading tenants');
          return [];
        }

        try {
          const activeTenant = useTenantState.getState().currentTenant;

          const response = await axios.get(env.apiTasklistBaseUrl + '/api/v1/tenant/tenants', {
            headers: {
              Authorization: `Bearer ${activeToken}`,
              'Content-Type': 'application/json',
              'X-Active-Tenant': activeTenant?.slug,
            },
          });

          if (response.status !== 200) {
            throw new Error('Failed to fetch tenants');
          }

          console.info('Tenants loaded successfully');
          set({ tenants: response.data.data });
          return response.data.data;
        } catch (error) {
          console.error('Error loading tenants:', error);
          set({ tenants: [] });
          throw error;
        }
      },

      handleLogin: async () => {
        // Use keycloak-js login method (handles PKCE internally)
        await keycloakAuth.login(globalThis.location.origin + '/callback');
      },
      
      handleLoginWithJoin: async (action, token) => {
        // Store invitation info for callback processing
        localStorage.setItem('invitation_action', action);
        localStorage.setItem('invitation_token', token);
        
        // Use keycloak-js login method
        await keycloakAuth.login(globalThis.location.origin + '/callback');
      },
      logout: async () => {
        set({ isLogoutLoading: true });

        try {
          // IMPORTANT: Call keycloak.logout() which handles server-side logout
          // This will redirect to Keycloak logout endpoint, then back to /login
          console.log('Initiating Keycloak logout...');
          await keycloakAuth.logout(undefined, globalThis.location.origin + '/login');
          
          // Clear state (in production, redirect happens before this executes)
          // But this is needed for test environments
          localStorage.removeItem('initial-tenant-id');
          localStorage.removeItem('auth-storage');
          localStorage.removeItem('current-tenant-storage');
          localStorage.removeItem('processes-storage');
          localStorage.removeItem('keycloak-auth');
          
          set({
            isLogoutLoading: false,
            userProfile: undefined,
            tenants: undefined,
            keycloakUserInfo: undefined,
          });
        } catch (error) {
          console.error('Error during logout:', error);
          
          // Cleanup on error
          localStorage.removeItem('initial-tenant-id');
          localStorage.removeItem('auth-storage');
          localStorage.removeItem('current-tenant-storage');
          localStorage.removeItem('processes-storage');
          localStorage.removeItem('keycloak-auth');
          
          set({
            isLogoutLoading: false,
            userProfile: undefined,
            tenants: undefined,
            keycloakUserInfo: undefined,
          });
          
          // Even if logout fails, redirect to login
          globalThis.location.href = `/login`;
        }
      },
      handleCallback: async () => {
        try {
          // With keycloak-js, tokens are already managed internally
          // We just need to get them and load user profile
          const token = await keycloakAuth.getValidToken();
          
          if (!token) {
            throw new Error('Failed to get token from Keycloak');
          }
          
          // Load user profile from backend API
          await get().loadUserProfile(token, true);
          
          // Try to load tenants (may not be available depending on setup)
          try {
            await get().loadTenants(token);
          } catch (error) {
            console.warn('Tenants not available:', error);
          }
        } catch (error) {
          console.error('Error during callback handling:', error);
          throw error;
        }
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: state => ({
        tenants: state.tenants,
        keycloakUserInfo: state.keycloakUserInfo,
        userProfile: state.userProfile,
        currentRole: state.currentRole,
      }),
    },
  ),
);

export default useAuthState;
